import React, { useState, useEffect } from 'react';
import { Flame, Droplets, AlertTriangle, Radio, Shield, CheckCircle } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ShopCategoryNav from '../components/ShopCategoryNav';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FirePage = () => {
  const [fireProducts, setFireProducts] = useState([]);
  const [waterProducts, setWaterProducts] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products?category=basic`);
        if (response.ok) {
          const data = await response.json();
          setFireProducts(data.filter(p => p.name.toLowerCase().includes('fireprotect')));
          setWaterProducts(data.filter(p => 
            p.name.toLowerCase().includes('leaksprotect') || 
            p.name.toLowerCase().includes('waterstop')
          ));
          setHubs(data.filter(p => p.name.toLowerCase().includes('hub')));
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categoryDefinitions = [
    { key: 'all', label: 'Tous les produits', icon: Shield },
    { key: 'fire', label: 'Incendie', icon: Flame, color: 'red' },
    { key: 'water', label: 'Inondation', icon: Droplets, color: 'blue' },
  ];

  const getFilteredProducts = () => {
    if (categoryFilter === 'fire') return fireProducts;
    if (categoryFilter === 'water') return waterProducts;
    return [...fireProducts, ...waterProducts];
  };

  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen py-12" data-testid="fire-page">
      <div className="pt-2">
        <ShopCategoryNav />
      </div>
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <div className="relative py-16 rounded-2xl overflow-hidden bg-gradient-to-r from-red-900/30 to-blue-900/30 border border-white/5">
          <div className="absolute inset-0 hero-glow opacity-30" />
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full mb-6">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-400 font-medium">Baseline — Protection Incendie & Inondation</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase mb-4">
              Baseline Incendie & Inondation
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Détecteurs d'incendie et d'inondation Ajax — Jusqu'à -20%
            </p>
            
            {/* Category Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              {categoryDefinitions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setCategoryFilter(key)}
                  data-testid={`category-${key}`}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                    categoryFilter === key
                      ? key === 'fire' 
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                        : key === 'water'
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-emerald-500 text-white shadow-lg shadow-fgs-primary/30'
                      : 'bg-white/[0.02] border border-white/5 text-white/70 hover:text-white hover:border-emerald-500/30'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {key !== 'all' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      categoryFilter === key ? 'bg-white/20' : 'bg-black/40'
                    }`}>
                      {key === 'fire' ? fireProducts.length : waterProducts.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice - Hub Required */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-6">
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Radio className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                Centrale Hub requise
              </h3>
              <p className="text-white/70">
                Les détecteurs d'incendie et d'inondation Ajax fonctionnent <strong className="text-amber-400">uniquement avec une centrale Hub Ajax</strong>. 
                Ils communiquent via le protocole radio Jeweller pour une fiabilité maximale et une autonomie de plusieurs années.
              </p>
            </div>
            <a 
              href="/shop"
              className="px-4 py-2 border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors whitespace-nowrap"
            >
              Voir les centrales Hub
            </a>
          </div>
        </div>
      </section>

      {/* Exclusivité Garantie 4 ans */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <div className="bg-gradient-to-r from-fgs-primary/10 to-emerald-900/20 border border-emerald-500/20 rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                <span className="text-emerald-400">Exclusivité FGS</span> — Garantie 4 ans
              </h3>
              <p className="text-white/70">
                Nous garantissons <strong className="text-emerald-400">tous les produits Ajax pendant 4 ans</strong>, 
                soit 2 ans de plus que la garantie constructeur standard. Votre tranquillité, notre engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Fire Detection Card */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 hover:border-red-500/50 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <Flame className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Détection Incendie</h3>
                <span className="text-red-400 text-sm">{fireProducts.length} produits</span>
              </div>
            </div>
            <p className="text-white/70 mb-4">
              Les détecteurs FireProtect détectent la fumée et les élévations rapides de température. 
              Sirène intégrée de 85 dB et notification instantanée sur smartphone.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                Détection fumée + chaleur
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                Sirène 85 dB intégrée
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                Autonomie jusqu'à 7 ans
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                Bouton de test intégré
              </li>
            </ul>
          </div>

          {/* Water Detection Card */}
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 hover:border-blue-500/50 transition-all">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Détection Inondation</h3>
                <span className="text-blue-400 text-sm">{waterProducts.length} produit{waterProducts.length > 1 ? 's' : ''}</span>
              </div>
            </div>
            <p className="text-white/70 mb-4">
              Le détecteur LeaksProtect détecte les fuites d'eau dès les premiers millimètres. 
              Idéal sous les éviers, machines à laver, chauffe-eau.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                Détection dès 0.5mm d'eau
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                4 points de contact
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                Autonomie jusqu'à 5 ans
              </li>
              <li className="flex items-center gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                Design ultra-plat
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-semibold text-white uppercase">
            {categoryFilter === 'fire' ? 'Détecteurs Incendie' : 
             categoryFilter === 'water' ? 'Détecteurs Inondation' : 
             'Tous les détecteurs'}
          </h2>
          <span className="text-white/60">{filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''}</span>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-xl">
            <AlertTriangle className="w-12 h-12 text-white/50 mx-auto mb-4" />
            <p className="text-white/60">Aucun produit trouvé</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard key={product.product_id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* Compatible Hubs Section */}
      {hubs.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Radio className="w-8 h-8 text-emerald-400" />
              <h2 className="text-2xl font-semibold text-white uppercase">
                Centrales compatibles
              </h2>
            </div>
            <p className="text-white/70 mb-6">
              Pour utiliser les détecteurs d'incendie et d'inondation, vous avez besoin d'une centrale Hub Ajax :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hubs.slice(0, 3).map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default FirePage;
